import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { UseBoolean } from 'react-hanger';
import { useBoolean } from 'react-hanger';
import { Button, DialogV2 } from 'c-components';
import { useCommonTranslation } from 'c-translation';
import { useAuthenticatedUser, useEntityPage, useLanguageSwitcher, useSkinSwitcher } from 'c-hooks';
import AdminSkinSwitcher from 'c-wrapper/Components/SkinLangSwitcher/AdminSkinSwitcher';
import ReportingConfigBar from 'c-main/Components/Campaign/ReportingConfigBar';
import {
    CUSTOM_REPORT_CONFIG_QUERY_KEY,
    encodeCustomReportConfig,
} from 'c-components/HiddenReport/Custom/customReportPreview';
import type {
    ChartSpec,
    CustomChartDataset,
    CustomChartMetric,
    CustomGroupByField,
    CustomReportConfig,
    CustomReportSlideConfig,
    ImpactMultiplierSlideConfig,
    OneMapSlideConfig,
    PerformanceBoxesSlideConfig,
    SlideLayoutConfig,
    SummaryCoverSlideConfig,
    TwoMapSlideConfig,
} from 'c-components/HiddenReport/Custom/customReportTypes';

import {
    Box,
    Checkbox,
    FormControlLabel,
    IconButton,
    MenuItem,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { Add, Delete, Palette, Visibility } from '@mui/icons-material';
import { LANG_en_gb } from 'c-lib';
import { DEFAULT_SKIN_ID } from 'c-wrapper/Slices/UI/ui-selectors';
import type { BaseConfigKey } from './pcaCustomizationLib';
import {
    baseConfigOptions,
    cloneConfig,
    createEmptySlide,
    getChartGroupByFields,
    getCurrentReportEnvironment,
    getPresetOptions,
    getPresetValue,
    getSlideLabel,
    isRunningTotalsChart,
    optionLabel,
    previewReportPayload,
    removeSlide,
    replaceSlideLayout,
    setChartGroupByFields,
    toLabel,
    toggleStringArrayValue,
} from './pcaCustomizationLib';

const chartDatasetOptions: CustomChartDataset[] = [
    'running_totals_impacts',
    'running_totals_plays',
    'display_owner',
    'display_env',
    'display_format',
    'display_env_format',
    'display_tv_region',
    'day_of_week',
    'hour_of_day',
];
const groupByFieldOptions: CustomGroupByField[] = [
    'display_owner',
    'display_env',
    'display_format',
    'display_tv_region',
    'display_conurbation',
    'day_of_week',
    'hour_of_day',
];
const multiGroupByFieldOptions: CustomGroupByField[] = [
    'display_owner',
    'display_env',
    'display_format',
    'display_tv_region',
    'display_conurbation',
];
const chartMetricOptions: CustomChartMetric[] = ['impacts', 'plays'];
const chartKindOptions: Array<NonNullable<ChartSpec['chartKind']>> = ['bar', 'line'];
const slideLayoutOptions: CustomReportSlideConfig['layout'][] = [
    'summaryCover',
    'linearTwoChart',
    'singleChart',
    'dspSplitChartInsights',
    'impactMultiplier',
    'oneMap',
    'twoMap',
    'glossary',
    'thankYou',
];
const summaryCardOptions = ['impacts', 'plays', 'time'] as const;
const summaryCardRowOptions = ['performance', 'total', 'expected', 'variance'] as const;

type Props = {
    dialogState?: UseBoolean;
    hideTrigger?: boolean;
};

const compactFieldSx = {
    '& .MuiInputBase-root': {
        minHeight: 40,
    },
};

const checkbox = (
    label: string,
    checked: boolean | undefined,
    onChange: (value: boolean) => void,
) => (
    <FormControlLabel
        sx={{ m: 0 }}
        control={<Checkbox checked={checked ?? false} onChange={e => onChange(e.target.checked)} />}
        label={label}
    />
);

const PCACustomizationDialog: React.FC<Props> = ({ dialogState, hideTrigger = false }) => {
    const t = useCommonTranslation();
    const { id } = useEntityPage();
    const numericCampaignId = Number(id);
    const { selectedSkin } = useSkinSwitcher();
    const { lang } = useLanguageSwitcher();
    const { user } = useAuthenticatedUser();
    const internalDialogState = useBoolean(false);
    const activeDialogState = dialogState ?? internalDialogState;
    const { value: isOpen, setTrue: open, setFalse: close } = activeDialogState;
    const [baseConfigKey, setBaseConfigKey] = useState<BaseConfigKey>('linear');
    const [draftConfig, setDraftConfig] = useState<CustomReportConfig>(cloneConfig('linear'));
    const [selectedSlideId, setSelectedSlideId] = useState<string | null>(null);
    const [newSlideLayout, setNewSlideLayout] =
        useState<CustomReportSlideConfig['layout']>('summaryCover');
    const [copied, setCopied] = useState(false);
    const [previewSkinId, setPreviewSkinId] = useState<number>(
        selectedSkin?.id != null && selectedSkin.id !== DEFAULT_SKIN_ID ? selectedSkin.id : 0,
    );
    const [hasExplicitPreviewSkin, setHasExplicitPreviewSkin] = useState(false);
    const previewDialogState = useBoolean(false);
    const previewSkinDialogState = useBoolean(false);
    const [previewUrl, setPreviewUrl] = useState('');
    const [previewSlideLabel, setPreviewSlideLabel] = useState('');
    const [pendingPreviewSlide, setPendingPreviewSlide] = useState<CustomReportSlideConfig | null>(
        null,
    );
    const previewIframeRef = useRef<HTMLIFrameElement | null>(null);

    useEffect(() => {
        if (!isOpen) return;
        const next = cloneConfig(baseConfigKey);
        setDraftConfig(next);
        setSelectedSlideId(next.slides[0]?.id ?? null);
        setCopied(false);
    }, [baseConfigKey, isOpen]);

    useEffect(() => {
        if (selectedSkin?.id != null && selectedSkin.id !== DEFAULT_SKIN_ID && !previewSkinId) {
            setPreviewSkinId(selectedSkin.id);
        }
    }, [previewSkinId, selectedSkin?.id]);

    const onPreviewSkinSelected = useCallback((skinId: number) => {
        setPreviewSkinId(skinId);
        setHasExplicitPreviewSkin(true);
    }, []);

    const selectedSlide = useMemo(
        () =>
            draftConfig.slides.find(slide => slide.id === selectedSlideId) ?? draftConfig.slides[0],
        [draftConfig.slides, selectedSlideId],
    );

    const updateSelectedSlide = useCallback(
        (updater: (slide: CustomReportSlideConfig) => CustomReportSlideConfig) => {
            if (!selectedSlide) return;
            setDraftConfig(current => ({
                ...current,
                slides: current.slides.map(slide =>
                    slide.id === selectedSlide.id ? updater(slide) : slide,
                ),
            }));
        },
        [selectedSlide],
    );

    const updateLayoutConfig = useCallback(
        (key: keyof SlideLayoutConfig, value: SlideLayoutConfig[keyof SlideLayoutConfig]) => {
            updateSelectedSlide(slide => ({
                ...slide,
                layoutConfig: {
                    ...slide.layoutConfig,
                    [key]: value,
                },
            }));
        },
        [updateSelectedSlide],
    );

    const updateChart = useCallback(
        (chartKey: 'chart' | 'left' | 'right', updater: (chart: ChartSpec) => ChartSpec) => {
            updateSelectedSlide(slide => {
                if (!(chartKey in slide)) return slide;
                const chart = slide[chartKey];
                if (chart == null || typeof chart !== 'object' || !('dataset' in chart))
                    return slide;
                return {
                    ...slide,
                    [chartKey]: updater(chart as ChartSpec),
                };
            });
        },
        [updateSelectedSlide],
    );

    const onOpenClicked = useCallback(
        (e: React.SyntheticEvent) => {
            e.preventDefault();
            e.stopPropagation();
            open();
        },
        [open],
    );

    const copyJson = useCallback(async () => {
        await navigator.clipboard?.writeText(JSON.stringify(draftConfig, null, 4));
        setCopied(true);
    }, [draftConfig]);

    const resetToBase = useCallback(() => {
        const next = cloneConfig(baseConfigKey);
        setDraftConfig(next);
        setSelectedSlideId(next.slides[0]?.id ?? null);
        setCopied(false);
    }, [baseConfigKey]);

    const addSlide = useCallback(() => {
        const slide = createEmptySlide(newSlideLayout);
        setDraftConfig(current => ({
            ...current,
            slides: [...current.slides, slide],
        }));
        setSelectedSlideId(slide.id);
    }, [newSlideLayout]);

    const removeSelectedSlide = useCallback(() => {
        if (!selectedSlide) return;

        setDraftConfig(current => {
            const { slides, selectedId } = removeSlide(current.slides, selectedSlide.id);
            setSelectedSlideId(selectedId);
            return {
                ...current,
                slides,
            };
        });
    }, [selectedSlide]);

    const buildPreviewUrl = useCallback(
        (previewConfig: CustomReportConfig) => {
            if (!numericCampaignId) return null;

            const payload = previewReportPayload({
                campaignId: numericCampaignId,
                previewSkinId,
                selectedSkinId: selectedSkin?.id,
                email: user?.email,
                britishEnglish: lang === LANG_en_gb,
                environment: getCurrentReportEnvironment(),
            });
            const params = new URLSearchParams({
                payload: JSON.stringify(payload),
                [CUSTOM_REPORT_CONFIG_QUERY_KEY]: encodeCustomReportConfig(previewConfig),
            });

            return `/hidden-report?${params.toString()}`;
        },
        [lang, numericCampaignId, previewSkinId, selectedSkin?.id, user?.email],
    );

    const openSlidePreview = useCallback(
        (slide: CustomReportSlideConfig) => {
            if (!hasExplicitPreviewSkin || !previewSkinId) {
                setPendingPreviewSlide(slide);
                previewSkinDialogState.setTrue();
                return;
            }

            const nextPreviewUrl = buildPreviewUrl({
                ...draftConfig,
                slides: [{ ...slide }],
            });
            if (!nextPreviewUrl) return;

            setPreviewUrl(nextPreviewUrl);
            setPreviewSlideLabel(getSlideLabel(slide));
            previewDialogState.setTrue();
        },
        [
            buildPreviewUrl,
            draftConfig,
            hasExplicitPreviewSkin,
            previewDialogState,
            previewSkinDialogState,
            previewSkinId,
        ],
    );

    const printSlidePreview = useCallback(() => {
        previewIframeRef.current?.contentWindow?.focus();
        previewIframeRef.current?.contentWindow?.print();
    }, []);

    const openFullPreview = useCallback(() => {
        if (!hasExplicitPreviewSkin || !previewSkinId) {
            setPendingPreviewSlide(null);
            previewSkinDialogState.setTrue();
            return;
        }

        const nextPreviewUrl = buildPreviewUrl(draftConfig);
        if (!nextPreviewUrl) return;

        setPreviewUrl(nextPreviewUrl);
        setPreviewSlideLabel(
            draftConfig.name || t('Modules.Main.Campaigns.Overview.PcaCustomization.fullPreview'),
        );
        previewDialogState.setTrue();
    }, [
        buildPreviewUrl,
        draftConfig,
        hasExplicitPreviewSkin,
        previewDialogState,
        previewSkinDialogState,
        previewSkinId,
        t,
    ]);

    const confirmPreviewSkinSelection = useCallback(() => {
        if (!previewSkinId) return;

        previewSkinDialogState.setFalse();
        const nextPreviewUrl = pendingPreviewSlide
            ? buildPreviewUrl({
                  ...draftConfig,
                  slides: [{ ...pendingPreviewSlide }],
              })
            : buildPreviewUrl(draftConfig);
        if (!nextPreviewUrl) return;

        setPreviewUrl(nextPreviewUrl);
        setPreviewSlideLabel(
            pendingPreviewSlide
                ? getSlideLabel(pendingPreviewSlide)
                : draftConfig.name ||
                      t('Modules.Main.Campaigns.Overview.PcaCustomization.fullPreview'),
        );
        setPendingPreviewSlide(null);
        previewDialogState.setTrue();
    }, [
        buildPreviewUrl,
        draftConfig,
        pendingPreviewSlide,
        previewDialogState,
        previewSkinDialogState,
        previewSkinId,
        t,
    ]);

    const renderChartEditor = (
        label: string,
        chartKey: 'chart' | 'left' | 'right',
        chart: ChartSpec,
    ) => {
        const groupByFields = getChartGroupByFields(chart);
        const isRunningTotals = isRunningTotalsChart(chart);
        const canAddGroupByField =
            !isRunningTotals &&
            groupByFields.length > 0 &&
            groupByFields.length < multiGroupByFieldOptions.length &&
            groupByFields.every(field => multiGroupByFieldOptions.includes(field));

        return (
            <Stack gap={1.25}>
                <Typography variant="subtitle2">{label}</Typography>
                <TextField
                    label={t('Modules.Main.Campaigns.Overview.PcaCustomization.chartTitle')}
                    value={chart.title}
                    onChange={e =>
                        updateChart(chartKey, current => ({ ...current, title: e.target.value }))
                    }
                    size="small"
                    fullWidth
                    sx={compactFieldSx}
                />
                <Stack direction={{ xs: 'column', md: 'row' }} gap={1}>
                    {isRunningTotals ? (
                        <TextField
                            select
                            label={t('Modules.Main.Campaigns.Overview.PcaCustomization.dataSource')}
                            value={chart.dataset}
                            onChange={e =>
                                updateChart(chartKey, current => ({
                                    ...current,
                                    dataset: e.target.value as CustomChartDataset,
                                    groupByFields: undefined,
                                }))
                            }
                            size="small"
                            fullWidth
                            sx={compactFieldSx}
                        >
                            {chartDatasetOptions
                                .filter(
                                    option =>
                                        option === 'running_totals_impacts' ||
                                        option === 'running_totals_plays',
                                )
                                .map(option => (
                                    <MenuItem key={option} value={option}>
                                        {optionLabel(option, t)}
                                    </MenuItem>
                                ))}
                        </TextField>
                    ) : (
                        <TextField
                            select
                            label={`${t(
                                'Modules.Main.Campaigns.Overview.PcaCustomization.groupByField',
                            )} 1`}
                            value={groupByFields[0] ?? ''}
                            onChange={e =>
                                updateChart(chartKey, current =>
                                    setChartGroupByFields(current, [
                                        e.target.value as CustomGroupByField,
                                    ]),
                                )
                            }
                            size="small"
                            fullWidth
                            sx={compactFieldSx}
                        >
                            {groupByFieldOptions.map(option => (
                                <MenuItem key={option} value={option}>
                                    {optionLabel(option, t)}
                                </MenuItem>
                            ))}
                        </TextField>
                    )}
                    <TextField
                        select
                        label={t('Modules.Main.Campaigns.Overview.PcaCustomization.metric')}
                        value={chart.metric}
                        onChange={e =>
                            updateChart(chartKey, current => ({
                                ...current,
                                metric: e.target.value as CustomChartMetric,
                            }))
                        }
                        size="small"
                        fullWidth
                        sx={compactFieldSx}
                    >
                        {chartMetricOptions.map(option => (
                            <MenuItem key={option} value={option}>
                                {optionLabel(option, t)}
                            </MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        select
                        label={t('Modules.Main.Campaigns.Overview.PcaCustomization.chartKind')}
                        value={chart.chartKind ?? 'bar'}
                        onChange={e =>
                            updateChart(chartKey, current => ({
                                ...current,
                                chartKind: e.target.value as NonNullable<ChartSpec['chartKind']>,
                            }))
                        }
                        size="small"
                        fullWidth
                        sx={compactFieldSx}
                    >
                        {chartKindOptions.map(option => (
                            <MenuItem key={option} value={option}>
                                {optionLabel(option, t)}
                            </MenuItem>
                        ))}
                    </TextField>
                </Stack>
                {!isRunningTotals && (
                    <Stack gap={1}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                            <Typography variant="subtitle2">
                                {t(
                                    'Modules.Main.Campaigns.Overview.PcaCustomization.groupByFields',
                                )}
                            </Typography>
                            {canAddGroupByField && (
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() =>
                                        updateChart(chartKey, current => {
                                            const currentFields = getChartGroupByFields(current);
                                            const nextField =
                                                multiGroupByFieldOptions.find(
                                                    option => !currentFields.includes(option),
                                                ) ?? multiGroupByFieldOptions[0];

                                            return setChartGroupByFields(current, [
                                                ...currentFields,
                                                nextField,
                                            ]);
                                        })
                                    }
                                >
                                    {t(
                                        'Modules.Main.Campaigns.Overview.PcaCustomization.addGroupBy',
                                    )}
                                </Button>
                            )}
                        </Stack>
                        {groupByFields.slice(1).map((field, index) => (
                            <Stack key={`${chartKey}-${field}`} direction="row" gap={1}>
                                <TextField
                                    select
                                    label={`${t(
                                        'Modules.Main.Campaigns.Overview.PcaCustomization.groupByField',
                                    )} ${index + 2}`}
                                    value={field}
                                    onChange={e =>
                                        updateChart(chartKey, current => {
                                            const currentFields = getChartGroupByFields(current);
                                            const nextField = e.target.value as CustomGroupByField;
                                            const nextFields = [...currentFields];
                                            nextFields[index + 1] = nextField;

                                            return setChartGroupByFields(current, nextFields);
                                        })
                                    }
                                    size="small"
                                    fullWidth
                                    sx={compactFieldSx}
                                >
                                    {(index === 0
                                        ? groupByFieldOptions
                                        : multiGroupByFieldOptions
                                    ).map(option => (
                                        <MenuItem key={option} value={option}>
                                            {optionLabel(option, t)}
                                        </MenuItem>
                                    ))}
                                </TextField>
                                {groupByFields.length > 1 && (
                                    <IconButton
                                        aria-label={t(
                                            'Modules.Main.Campaigns.Overview.PcaCustomization.removeGroupBy',
                                        )}
                                        onClick={() =>
                                            updateChart(chartKey, current => {
                                                const currentFields =
                                                    getChartGroupByFields(current);
                                                return setChartGroupByFields(
                                                    current,
                                                    currentFields.filter((_, i) => i !== index + 1),
                                                );
                                            })
                                        }
                                        sx={{ alignSelf: 'center' }}
                                    >
                                        <Delete fontSize="small" />
                                    </IconButton>
                                )}
                            </Stack>
                        ))}
                    </Stack>
                )}
                <Stack direction={{ xs: 'column', md: 'row' }} gap={1}>
                    <TextField
                        type="number"
                        label={t('Modules.Main.Campaigns.Overview.PcaCustomization.maxItems')}
                        value={chart.maxItems ?? ''}
                        onChange={e =>
                            updateChart(chartKey, current => ({
                                ...current,
                                maxItems:
                                    e.target.value === '' ? undefined : Number(e.target.value),
                            }))
                        }
                        size="small"
                        fullWidth
                        sx={compactFieldSx}
                    />
                    <TextField
                        type="number"
                        label={t('Modules.Main.Campaigns.Overview.PcaCustomization.fontSize')}
                        value={chart.fontSize ?? ''}
                        onChange={e =>
                            updateChart(chartKey, current => ({
                                ...current,
                                fontSize:
                                    e.target.value === '' ? undefined : Number(e.target.value),
                            }))
                        }
                        size="small"
                        fullWidth
                        sx={compactFieldSx}
                    />
                </Stack>
                <Stack direction={{ xs: 'column', md: 'row' }} gap={1} flexWrap="wrap">
                    {checkbox(
                        t('Modules.Main.Campaigns.Overview.PcaCustomization.showTriplet'),
                        chart.showTriplet,
                        value =>
                            updateChart(chartKey, current => ({ ...current, showTriplet: value })),
                    )}
                    {checkbox(
                        t('Modules.Main.Campaigns.Overview.PcaCustomization.showValueLabels'),
                        chart.showValueLabels,
                        value =>
                            updateChart(chartKey, current => ({
                                ...current,
                                showValueLabels: value,
                            })),
                    )}
                    {checkbox(
                        t('Modules.Main.Campaigns.Overview.PcaCustomization.showExpected'),
                        // Expected is drawn unless explicitly turned off, so unset reads as checked.
                        chart.showExpected !== false,
                        value =>
                            updateChart(chartKey, current => ({ ...current, showExpected: value })),
                    )}
                    {checkbox(
                        t('Modules.Main.Campaigns.Overview.PcaCustomization.includeZeroValues'),
                        chart.includeZeroValues,
                        value =>
                            updateChart(chartKey, current => ({
                                ...current,
                                includeZeroValues: value,
                            })),
                    )}
                </Stack>
            </Stack>
        );
    };

    const renderPerformanceBoxesEditor = (slide: PerformanceBoxesSlideConfig) => (
        <Stack gap={1.25}>
            <TextField
                label={t('Modules.Main.Campaigns.Overview.PcaCustomization.mainTitle')}
                value={slide.mainTitle}
                onChange={e =>
                    updateSelectedSlide(current => ({ ...current, mainTitle: e.target.value }))
                }
                size="small"
                fullWidth
                sx={compactFieldSx}
            />
            <Stack direction={{ xs: 'column', md: 'row' }} gap={1}>
                <TextField
                    label={t('Modules.Main.Campaigns.Overview.PcaCustomization.title')}
                    value={slide.title}
                    onChange={e =>
                        updateSelectedSlide(current => ({ ...current, title: e.target.value }))
                    }
                    size="small"
                    fullWidth
                    sx={compactFieldSx}
                />
                <TextField
                    select
                    label={t('Modules.Main.Campaigns.Overview.PcaCustomization.dataset')}
                    value={slide.dataset}
                    onChange={e =>
                        updateSelectedSlide(current => ({
                            ...current,
                            dataset: e.target.value as PerformanceBoxesSlideConfig['dataset'],
                        }))
                    }
                    size="small"
                    fullWidth
                    sx={compactFieldSx}
                >
                    {chartDatasetOptions
                        .filter(option => !option.startsWith('running_totals'))
                        .map(option => (
                            <MenuItem key={option} value={option}>
                                {optionLabel(option, t)}
                            </MenuItem>
                        ))}
                </TextField>
                <TextField
                    select
                    label={t('Modules.Main.Campaigns.Overview.PcaCustomization.metric')}
                    value={slide.metric}
                    onChange={e =>
                        updateSelectedSlide(current => ({
                            ...current,
                            metric: e.target.value as PerformanceBoxesSlideConfig['metric'],
                        }))
                    }
                    size="small"
                    fullWidth
                    sx={compactFieldSx}
                >
                    {chartMetricOptions.map(option => (
                        <MenuItem key={option} value={option}>
                            {optionLabel(option, t)}
                        </MenuItem>
                    ))}
                </TextField>
            </Stack>
            <Stack direction={{ xs: 'column', md: 'row' }} gap={1}>
                <TextField
                    type="number"
                    label={t('Modules.Main.Campaigns.Overview.PcaCustomization.maxItems')}
                    value={slide.maxItems ?? ''}
                    onChange={e =>
                        updateSelectedSlide(current => ({
                            ...current,
                            maxItems: e.target.value === '' ? undefined : Number(e.target.value),
                        }))
                    }
                    size="small"
                    fullWidth
                    sx={compactFieldSx}
                />
                <TextField
                    select
                    label={t('Modules.Main.Campaigns.Overview.PcaCustomization.columns')}
                    value={slide.columns ?? 3}
                    onChange={e =>
                        updateSelectedSlide(current => ({
                            ...current,
                            columns: Number(
                                e.target.value,
                            ) as PerformanceBoxesSlideConfig['columns'],
                        }))
                    }
                    size="small"
                    fullWidth
                    sx={compactFieldSx}
                >
                    {[2, 3, 4].map(option => (
                        <MenuItem key={option} value={option}>
                            {option}
                        </MenuItem>
                    ))}
                </TextField>
                <TextField
                    select
                    label={t('Modules.Main.Campaigns.Overview.PcaCustomization.sortBy')}
                    value={slide.sortBy ?? 'performance'}
                    onChange={e =>
                        updateSelectedSlide(current => ({
                            ...current,
                            sortBy: e.target.value as PerformanceBoxesSlideConfig['sortBy'],
                        }))
                    }
                    size="small"
                    fullWidth
                    sx={compactFieldSx}
                >
                    {['performance', 'actual', 'variance'].map(option => (
                        <MenuItem key={option} value={option}>
                            {optionLabel(option, t)}
                        </MenuItem>
                    ))}
                </TextField>
            </Stack>
        </Stack>
    );

    const renderImpactMultiplierEditor = (slide: ImpactMultiplierSlideConfig) => (
        <Stack gap={1.25}>
            <Stack direction={{ xs: 'column', md: 'row' }} gap={1}>
                <TextField
                    select
                    label={t('Modules.Main.Campaigns.Overview.PcaCustomization.preset')}
                    value={getPresetValue(slide)}
                    onChange={e =>
                        updateSelectedSlide(current => ({
                            ...(current as ImpactMultiplierSlideConfig),
                            preset: e.target.value as ImpactMultiplierSlideConfig['preset'],
                        }))
                    }
                    size="small"
                    fullWidth
                    sx={compactFieldSx}
                >
                    {['maintainGrow', 'reviewConsider'].map(option => (
                        <MenuItem key={option} value={option}>
                            {optionLabel(option, t)}
                        </MenuItem>
                    ))}
                </TextField>
                <TextField
                    type="number"
                    label={t('Modules.Main.Campaigns.Overview.PcaCustomization.maxItems')}
                    value={slide.maxRows ?? ''}
                    onChange={e =>
                        updateSelectedSlide(current => ({
                            ...(current as ImpactMultiplierSlideConfig),
                            maxRows: e.target.value === '' ? undefined : Number(e.target.value),
                        }))
                    }
                    size="small"
                    fullWidth
                    sx={compactFieldSx}
                />
            </Stack>
            <TextField
                label={t('Modules.Main.Campaigns.Overview.PcaCustomization.title')}
                value={slide.title ?? ''}
                onChange={e =>
                    updateSelectedSlide(current => ({
                        ...(current as ImpactMultiplierSlideConfig),
                        title: e.target.value,
                    }))
                }
                size="small"
                fullWidth
                sx={compactFieldSx}
            />
            <TextField
                label={t('Modules.Main.Campaigns.Overview.PcaCustomization.subtitle')}
                value={slide.subtitle ?? ''}
                onChange={e =>
                    updateSelectedSlide(current => ({
                        ...(current as ImpactMultiplierSlideConfig),
                        subtitle: e.target.value,
                    }))
                }
                size="small"
                fullWidth
                sx={compactFieldSx}
            />
        </Stack>
    );

    const renderSelectedSlideEditor = () => {
        if (!selectedSlide) return null;

        return (
            <Stack gap={1.5}>
                <Stack direction={{ xs: 'column', md: 'row' }} gap={1}>
                    <TextField
                        label={t('Modules.Main.Campaigns.Overview.PcaCustomization.slideId')}
                        value={selectedSlide.id}
                        size="small"
                        fullWidth
                        disabled
                        sx={compactFieldSx}
                    />
                    <TextField
                        label={t('Modules.Main.Campaigns.Overview.PcaCustomization.layout')}
                        value={selectedSlide.layout}
                        select
                        onChange={e =>
                            updateSelectedSlide(current =>
                                replaceSlideLayout(
                                    current,
                                    e.target.value as CustomReportSlideConfig['layout'],
                                ),
                            )
                        }
                        size="small"
                        fullWidth
                        sx={compactFieldSx}
                    >
                        {slideLayoutOptions.map(option => (
                            <MenuItem key={option} value={option}>
                                {optionLabel(option, t)}
                            </MenuItem>
                        ))}
                    </TextField>
                    {getPresetOptions(selectedSlide).length > 0 && (
                        <TextField
                            select
                            label={t('Modules.Main.Campaigns.Overview.PcaCustomization.preset')}
                            value={getPresetValue(selectedSlide)}
                            onChange={e =>
                                updateSelectedSlide(current => ({
                                    ...current,
                                    preset: e.target.value as never,
                                }))
                            }
                            size="small"
                            fullWidth
                            sx={compactFieldSx}
                        >
                            {getPresetOptions(selectedSlide).map(option => (
                                <MenuItem key={option} value={option}>
                                    {optionLabel(option, t)}
                                </MenuItem>
                            ))}
                        </TextField>
                    )}
                </Stack>

                <Stack direction={{ xs: 'column', md: 'row' }} gap={1} flexWrap="wrap">
                    {checkbox(
                        t('Modules.Main.Campaigns.Overview.PcaCustomization.includeSlide'),
                        selectedSlide.enabled,
                        value => updateSelectedSlide(current => ({ ...current, enabled: value })),
                    )}
                </Stack>

                <Stack direction={{ xs: 'column', md: 'row' }} gap={1}>
                    <TextField
                        label={t('Modules.Main.Campaigns.Overview.PcaCustomization.splitWidth')}
                        value={selectedSlide.layoutConfig?.splitWidth ?? ''}
                        onChange={e => updateLayoutConfig('splitWidth', e.target.value)}
                        size="small"
                        fullWidth
                        sx={compactFieldSx}
                    />
                    <TextField
                        select
                        label={t('Modules.Main.Campaigns.Overview.PcaCustomization.footerColour')}
                        value={selectedSlide.layoutConfig?.footerBrandVariant ?? 'primary'}
                        onChange={e =>
                            updateLayoutConfig(
                                'footerBrandVariant',
                                e.target.value as SlideLayoutConfig['footerBrandVariant'],
                            )
                        }
                        size="small"
                        fullWidth
                        sx={compactFieldSx}
                    >
                        <MenuItem value="primary">
                            {t('Modules.Main.Campaigns.Overview.PcaCustomization.primary')}
                        </MenuItem>
                        <MenuItem value="accent">
                            {t('Modules.Main.Campaigns.Overview.PcaCustomization.thirdColour')}
                        </MenuItem>
                    </TextField>
                    <TextField
                        select
                        label={t('Modules.Main.Campaigns.Overview.PcaCustomization.slideColour')}
                        value={selectedSlide.layoutConfig?.brandVariant ?? 'primary'}
                        onChange={e =>
                            updateLayoutConfig(
                                'brandVariant',
                                e.target.value as SlideLayoutConfig['brandVariant'],
                            )
                        }
                        size="small"
                        fullWidth
                        sx={compactFieldSx}
                    >
                        <MenuItem value="primary">
                            {t('Modules.Main.Campaigns.Overview.PcaCustomization.primary')}
                        </MenuItem>
                        <MenuItem value="accent">
                            {t('Modules.Main.Campaigns.Overview.PcaCustomization.thirdColour')}
                        </MenuItem>
                    </TextField>
                </Stack>

                {selectedSlide.layout === 'summaryCover' && (
                    <Stack gap={1.25}>
                        <TextField
                            label={t('Modules.Main.Campaigns.Overview.PcaCustomization.title')}
                            value={selectedSlide.title ?? ''}
                            onChange={e =>
                                updateSelectedSlide(current => ({
                                    ...current,
                                    title: e.target.value,
                                }))
                            }
                            size="small"
                            fullWidth
                            sx={compactFieldSx}
                        />
                        <Stack direction={{ xs: 'column', md: 'row' }} gap={1}>
                            {checkbox(
                                t(
                                    'Modules.Main.Campaigns.Overview.PcaCustomization.showPerformanceHeadlines',
                                ),
                                selectedSlide.showPerformanceHeadlines,
                                value =>
                                    updateSelectedSlide(current => ({
                                        ...current,
                                        showPerformanceHeadlines: value,
                                    })),
                            )}
                            {checkbox(
                                t(
                                    'Modules.Main.Campaigns.Overview.PcaCustomization.showCallToAction',
                                ),
                                selectedSlide.showCta,
                                value =>
                                    updateSelectedSlide(current => ({
                                        ...current,
                                        showCta: value,
                                    })),
                            )}
                        </Stack>
                        <Stack gap={0.5}>
                            <Typography variant="subtitle2">
                                {t('Modules.Main.Campaigns.Overview.PcaCustomization.summaryCards')}
                            </Typography>
                            <Stack direction={{ xs: 'column', md: 'row' }} gap={1} flexWrap="wrap">
                                {summaryCardOptions.map(option =>
                                    checkbox(
                                        optionLabel(option, t),
                                        selectedSlide.summaryCards?.includes(option) ??
                                            !selectedSlide.summaryCards,
                                        value =>
                                            updateSelectedSlide(current => ({
                                                ...(current as SummaryCoverSlideConfig),
                                                summaryCards: toggleStringArrayValue(
                                                    (current as SummaryCoverSlideConfig)
                                                        .summaryCards,
                                                    option,
                                                    value,
                                                ),
                                                headlineCount: undefined,
                                            })),
                                    ),
                                )}
                            </Stack>
                        </Stack>
                        <Stack gap={0.5}>
                            <Typography variant="subtitle2">
                                {t(
                                    'Modules.Main.Campaigns.Overview.PcaCustomization.summaryCardRows',
                                )}
                            </Typography>
                            <Stack direction={{ xs: 'column', md: 'row' }} gap={1} flexWrap="wrap">
                                {summaryCardRowOptions.map(option =>
                                    checkbox(
                                        optionLabel(option, t),
                                        selectedSlide.summaryCardRows?.includes(option) ??
                                            !selectedSlide.summaryCardRows,
                                        value =>
                                            updateSelectedSlide(current => ({
                                                ...(current as SummaryCoverSlideConfig),
                                                summaryCardRows: toggleStringArrayValue(
                                                    (current as SummaryCoverSlideConfig)
                                                        .summaryCardRows,
                                                    option,
                                                    value,
                                                ),
                                            })),
                                    ),
                                )}
                            </Stack>
                        </Stack>
                    </Stack>
                )}

                {selectedSlide.layout === 'linearTwoChart' && (
                    <Stack gap={2}>
                        <TextField
                            label={t('Modules.Main.Campaigns.Overview.PcaCustomization.mainTitle')}
                            value={selectedSlide.mainTitle}
                            onChange={e =>
                                updateSelectedSlide(current => ({
                                    ...current,
                                    mainTitle: e.target.value,
                                }))
                            }
                            size="small"
                            fullWidth
                            sx={compactFieldSx}
                        />
                        {renderChartEditor(
                            t('Modules.Main.Campaigns.Overview.PcaCustomization.leftChart'),
                            'left',
                            selectedSlide.left,
                        )}
                        {renderChartEditor(
                            t('Modules.Main.Campaigns.Overview.PcaCustomization.rightChart'),
                            'right',
                            selectedSlide.right,
                        )}
                    </Stack>
                )}

                {selectedSlide.layout === 'singleChart' && (
                    <Stack gap={2}>
                        <TextField
                            label={t('Modules.Main.Campaigns.Overview.PcaCustomization.mainTitle')}
                            value={selectedSlide.mainTitle}
                            onChange={e =>
                                updateSelectedSlide(current => ({
                                    ...current,
                                    mainTitle: e.target.value,
                                }))
                            }
                            size="small"
                            fullWidth
                            sx={compactFieldSx}
                        />
                        {renderChartEditor(
                            t('Modules.Main.Campaigns.Overview.PcaCustomization.chart'),
                            'chart',
                            selectedSlide.chart,
                        )}
                    </Stack>
                )}

                {selectedSlide.layout === 'dspSplitChartInsights' && (
                    <Stack gap={2}>
                        <TextField
                            label={t('Modules.Main.Campaigns.Overview.PcaCustomization.heading')}
                            value={selectedSlide.heading}
                            onChange={e =>
                                updateSelectedSlide(current => ({
                                    ...current,
                                    heading: e.target.value,
                                }))
                            }
                            size="small"
                            fullWidth
                            sx={compactFieldSx}
                        />
                        <Stack direction={{ xs: 'column', md: 'row' }} gap={1}>
                            <TextField
                                type="number"
                                label={t(
                                    'Modules.Main.Campaigns.Overview.PcaCustomization.chartHeight',
                                )}
                                value={selectedSlide.chartHeight ?? ''}
                                onChange={e =>
                                    updateSelectedSlide(current => ({
                                        ...current,
                                        chartHeight:
                                            e.target.value === ''
                                                ? undefined
                                                : Number(e.target.value),
                                    }))
                                }
                                size="small"
                                fullWidth
                                sx={compactFieldSx}
                            />
                            <TextField
                                label={t(
                                    'Modules.Main.Campaigns.Overview.PcaCustomization.leftPillText',
                                )}
                                value={selectedSlide.leftPillText ?? ''}
                                onChange={e =>
                                    updateSelectedSlide(current => ({
                                        ...current,
                                        leftPillText: e.target.value,
                                    }))
                                }
                                size="small"
                                fullWidth
                                sx={compactFieldSx}
                            />
                            <TextField
                                label={t(
                                    'Modules.Main.Campaigns.Overview.PcaCustomization.rightPillText',
                                )}
                                value={selectedSlide.rightPillText ?? ''}
                                onChange={e =>
                                    updateSelectedSlide(current => ({
                                        ...current,
                                        rightPillText: e.target.value,
                                    }))
                                }
                                size="small"
                                fullWidth
                                sx={compactFieldSx}
                            />
                        </Stack>
                        {renderChartEditor(
                            t('Modules.Main.Campaigns.Overview.PcaCustomization.chart'),
                            'chart',
                            selectedSlide.chart,
                        )}
                        {selectedSlide.insights?.kind === 'dayNightShare' &&
                            checkbox(
                                'Use 12-hour time format (e.g. 5 AM – 11 PM)',
                                (selectedSlide.insights as any).hourFormat === '12h',
                                value =>
                                    updateSelectedSlide(current => ({
                                        ...current,
                                        insights: {
                                            ...(current as any).insights,
                                            hourFormat: value ? '12h' : undefined,
                                        },
                                    })),
                            )}
                    </Stack>
                )}

                {selectedSlide.layout === 'performanceBoxes' &&
                    renderPerformanceBoxesEditor(selectedSlide)}

                {selectedSlide.layout === 'impactMultiplier' &&
                    renderImpactMultiplierEditor(selectedSlide)}

                {selectedSlide.layout === 'oneMap' && (
                    <Stack gap={1.25}>
                        <TextField
                            label={t('Modules.Main.Campaigns.Overview.PcaCustomization.mainTitle')}
                            value={selectedSlide.mainTitle}
                            onChange={e =>
                                updateSelectedSlide(current => ({
                                    ...current,
                                    mainTitle: e.target.value,
                                }))
                            }
                            size="small"
                            fullWidth
                            sx={compactFieldSx}
                        />
                        <Stack direction={{ xs: 'column', md: 'row' }} gap={1}>
                            <TextField
                                label={t(
                                    'Modules.Main.Campaigns.Overview.PcaCustomization.mapTitle',
                                )}
                                value={selectedSlide.map.title}
                                onChange={e =>
                                    updateSelectedSlide(current => ({
                                        ...(current as OneMapSlideConfig),
                                        map: {
                                            ...(current as OneMapSlideConfig).map,
                                            title: e.target.value,
                                        },
                                    }))
                                }
                                size="small"
                                fullWidth
                                sx={compactFieldSx}
                            />
                            <TextField
                                select
                                label={t(
                                    'Modules.Main.Campaigns.Overview.PcaCustomization.mapMode',
                                )}
                                value={selectedSlide.map.mode}
                                onChange={e =>
                                    updateSelectedSlide(current => ({
                                        ...(current as OneMapSlideConfig),
                                        map: {
                                            ...(current as OneMapSlideConfig).map,
                                            mode: e.target.value as 'heat' | 'markers',
                                        },
                                    }))
                                }
                                size="small"
                                fullWidth
                                sx={compactFieldSx}
                            >
                                <MenuItem value="heat">
                                    {t('Modules.Main.Campaigns.Overview.PcaCustomization.heat')}
                                </MenuItem>
                                <MenuItem value="markers">
                                    {t('Modules.Main.Campaigns.Overview.PcaCustomization.markers')}
                                </MenuItem>
                            </TextField>
                        </Stack>
                    </Stack>
                )}

                {selectedSlide.layout === 'twoMap' && (
                    <Stack gap={1.25}>
                        <TextField
                            label={t('Modules.Main.Campaigns.Overview.PcaCustomization.mainTitle')}
                            value={selectedSlide.mainTitle}
                            onChange={e =>
                                updateSelectedSlide(current => ({
                                    ...current,
                                    mainTitle: e.target.value,
                                }))
                            }
                            size="small"
                            fullWidth
                            sx={compactFieldSx}
                        />
                        <Typography variant="subtitle2">
                            {t('Modules.Main.Campaigns.Overview.PcaCustomization.leftMap')}
                        </Typography>
                        <Stack direction={{ xs: 'column', md: 'row' }} gap={1}>
                            <TextField
                                label={t(
                                    'Modules.Main.Campaigns.Overview.PcaCustomization.mapTitle',
                                )}
                                value={selectedSlide.left.title}
                                onChange={e =>
                                    updateSelectedSlide(current => ({
                                        ...(current as TwoMapSlideConfig),
                                        left: {
                                            ...(current as TwoMapSlideConfig).left,
                                            title: e.target.value,
                                        },
                                    }))
                                }
                                size="small"
                                fullWidth
                                sx={compactFieldSx}
                            />
                            <TextField
                                select
                                label={t(
                                    'Modules.Main.Campaigns.Overview.PcaCustomization.mapMode',
                                )}
                                value={selectedSlide.left.mode}
                                onChange={e =>
                                    updateSelectedSlide(current => ({
                                        ...(current as TwoMapSlideConfig),
                                        left: {
                                            ...(current as TwoMapSlideConfig).left,
                                            mode: e.target.value as 'heat' | 'markers',
                                        },
                                    }))
                                }
                                size="small"
                                fullWidth
                                sx={compactFieldSx}
                            >
                                <MenuItem value="heat">
                                    {t('Modules.Main.Campaigns.Overview.PcaCustomization.heat')}
                                </MenuItem>
                                <MenuItem value="markers">
                                    {t('Modules.Main.Campaigns.Overview.PcaCustomization.markers')}
                                </MenuItem>
                            </TextField>
                        </Stack>
                        <Typography variant="subtitle2">
                            {t('Modules.Main.Campaigns.Overview.PcaCustomization.rightMap')}
                        </Typography>
                        <Stack direction={{ xs: 'column', md: 'row' }} gap={1}>
                            <TextField
                                label={t(
                                    'Modules.Main.Campaigns.Overview.PcaCustomization.mapTitle',
                                )}
                                value={selectedSlide.right.title}
                                onChange={e =>
                                    updateSelectedSlide(current => ({
                                        ...(current as TwoMapSlideConfig),
                                        right: {
                                            ...(current as TwoMapSlideConfig).right,
                                            title: e.target.value,
                                        },
                                    }))
                                }
                                size="small"
                                fullWidth
                                sx={compactFieldSx}
                            />
                            <TextField
                                select
                                label={t(
                                    'Modules.Main.Campaigns.Overview.PcaCustomization.mapMode',
                                )}
                                value={selectedSlide.right.mode}
                                onChange={e =>
                                    updateSelectedSlide(current => ({
                                        ...(current as TwoMapSlideConfig),
                                        right: {
                                            ...(current as TwoMapSlideConfig).right,
                                            mode: e.target.value as 'heat' | 'markers',
                                        },
                                    }))
                                }
                                size="small"
                                fullWidth
                                sx={compactFieldSx}
                            >
                                <MenuItem value="heat">
                                    {t('Modules.Main.Campaigns.Overview.PcaCustomization.heat')}
                                </MenuItem>
                                <MenuItem value="markers">
                                    {t('Modules.Main.Campaigns.Overview.PcaCustomization.markers')}
                                </MenuItem>
                            </TextField>
                        </Stack>
                    </Stack>
                )}

                {selectedSlide.layout === 'glossary' && (
                    <TextField
                        label={t('Modules.Main.Campaigns.Overview.PcaCustomization.title')}
                        value={selectedSlide.title ?? ''}
                        onChange={e =>
                            updateSelectedSlide(current => ({ ...current, title: e.target.value }))
                        }
                        size="small"
                        fullWidth
                        sx={compactFieldSx}
                    />
                )}

                {selectedSlide.layout === 'thankYou' && (
                    <Stack gap={1.25}>
                        <TextField
                            label={t('Modules.Main.Campaigns.Overview.PcaCustomization.title')}
                            value={selectedSlide.title ?? ''}
                            onChange={e =>
                                updateSelectedSlide(current => ({
                                    ...current,
                                    title: e.target.value,
                                }))
                            }
                            size="small"
                            fullWidth
                            sx={compactFieldSx}
                        />
                        <TextField
                            label={t('Modules.Main.Campaigns.Overview.PcaCustomization.subtitle')}
                            value={selectedSlide.subtitle ?? ''}
                            onChange={e =>
                                updateSelectedSlide(current => ({
                                    ...current,
                                    subtitle: e.target.value,
                                }))
                            }
                            size="small"
                            fullWidth
                            sx={compactFieldSx}
                        />
                    </Stack>
                )}
            </Stack>
        );
    };

    return (
        <>
            {!hideTrigger && (
                <Tooltip
                    title={t('Modules.Main.Campaigns.Overview.ActionLabels.customizePca')}
                    arrow
                >
                    <IconButton onMouseDown={onOpenClicked} onClick={onOpenClicked}>
                        <Palette fontSize="inherit" />
                    </IconButton>
                </Tooltip>
            )}
            <DialogV2
                open={previewSkinDialogState.value}
                onClose={() => {
                    setPendingPreviewSlide(null);
                    previewSkinDialogState.setFalse();
                }}
                maxWidth="sm"
                title="Modules.Main.Campaigns.Overview.PcaCustomization.choosePreviewSkin"
                actions={
                    <Stack direction="row" gap={1}>
                        <Button
                            onClick={() => {
                                setPendingPreviewSlide(null);
                                previewSkinDialogState.setFalse();
                            }}
                        >
                            {t('Dialog.defaultCancelLabel')}
                        </Button>
                        <Button onClick={confirmPreviewSkinSelection} disabled={!previewSkinId}>
                            {t('Modules.Main.Campaigns.Overview.PcaCustomization.continuePreview')}
                        </Button>
                    </Stack>
                }
            >
                <Stack gap={1}>
                    <Typography variant="body2" color="text.secondary">
                        {t(
                            'Modules.Main.Campaigns.Overview.PcaCustomization.choosePreviewSkinHint',
                        )}
                    </Typography>
                    <AdminSkinSwitcher
                        disabled={false}
                        onChange={onPreviewSkinSelected}
                        additionalOptions={[]}
                        selectedSkinId={previewSkinId || DEFAULT_SKIN_ID}
                    />
                </Stack>
            </DialogV2>
            <DialogV2
                open={previewDialogState.value}
                onClose={previewDialogState.setFalse}
                maxWidth="xl"
                title={
                    previewSlideLabel ||
                    'Modules.Main.Campaigns.Overview.PcaCustomization.previewSlide'
                }
                actions={
                    <Stack direction="row" gap={1}>
                        <Button onClick={printSlidePreview}>
                            {t('Modules.Main.Campaigns.Overview.PcaCustomization.printPreview')}
                        </Button>
                    </Stack>
                }
                MuiDialogContentProps={{
                    sx: {
                        minHeight: '80vh',
                    },
                }}
            >
                <Box
                    sx={{
                        flex: 1,
                        minHeight: '70vh',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1.5,
                        overflow: 'hidden',
                        bgcolor: 'common.white',
                    }}
                >
                    {previewUrl && (
                        <iframe
                            ref={previewIframeRef}
                            title={previewSlideLabel || 'PCA slide preview'}
                            src={previewUrl}
                            width="100%"
                            height="100%"
                            style={{ border: 0, display: 'block' }}
                        />
                    )}
                </Box>
            </DialogV2>
            <DialogV2
                open={isOpen}
                onClose={close}
                maxWidth="xl"
                title="Modules.Main.Campaigns.Overview.ActionLabels.customizePca"
                actions={
                    <Stack direction="row" gap={1}>
                        <Button onClick={openFullPreview}>
                            {t(
                                'Modules.Main.Campaigns.Overview.PcaCustomization.previewFullReport',
                            )}
                        </Button>
                        <Button onClick={resetToBase}>
                            {t('Modules.Main.Campaigns.Overview.PcaCustomization.resetToBase')}
                        </Button>
                        <Button onClick={copyJson}>
                            {copied
                                ? t('Modules.Main.Campaigns.Overview.PcaCustomization.copied')
                                : t('Modules.Main.Campaigns.Overview.PcaCustomization.copyJson')}
                        </Button>
                    </Stack>
                }
                MuiDialogContentProps={{
                    sx: {
                        minHeight: '72vh',
                    },
                }}
            >
                <Stack gap={2} flex={1}>
                    <ReportingConfigBar
                        type="pca"
                        campaignType={baseConfigKey === 'dsp' ? 'dsp' : 'linear'}
                        getConfig={() => JSON.stringify(draftConfig)}
                        onLoad={config => {
                            try {
                                setDraftConfig(JSON.parse(config) as CustomReportConfig);
                            } catch {
                                setDraftConfig(current => current);
                            }
                        }}
                    />
                    <Stack direction={{ xs: 'column', lg: 'row' }} gap={2} flex={1}>
                        <Stack gap={1.25} sx={{ width: { xs: '100%', lg: 310 }, flexShrink: 0 }}>
                            <Typography variant="h5">
                                {t(
                                    'Modules.Main.Campaigns.Overview.PcaCustomization.reportSettings',
                                )}
                            </Typography>
                            <TextField
                                label={t(
                                    'Modules.Main.Campaigns.Overview.PcaCustomization.reportName',
                                )}
                                value={draftConfig.name}
                                onChange={e =>
                                    setDraftConfig(current => ({
                                        ...current,
                                        name: e.target.value,
                                    }))
                                }
                                size="small"
                                fullWidth
                                sx={compactFieldSx}
                            />
                            <Stack>
                                <AdminSkinSwitcher
                                    disabled={false}
                                    onChange={onPreviewSkinSelected}
                                    additionalOptions={[]}
                                    selectedSkinId={previewSkinId || DEFAULT_SKIN_ID}
                                />
                            </Stack>
                            <TextField
                                select
                                label={t(
                                    'Modules.Main.Campaigns.Overview.PcaCustomization.startFrom',
                                )}
                                value={baseConfigKey}
                                onChange={e => setBaseConfigKey(e.target.value as BaseConfigKey)}
                                size="small"
                                fullWidth
                                sx={compactFieldSx}
                            >
                                {baseConfigOptions.map(option => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </TextField>

                            <Typography variant="h5" sx={{ mt: 1 }}>
                                {t('Modules.Main.Campaigns.Overview.PcaCustomization.slides')}
                            </Typography>
                            <Stack direction={{ xs: 'column', md: 'row', lg: 'column' }} gap={1}>
                                <TextField
                                    select
                                    label={t(
                                        'Modules.Main.Campaigns.Overview.PcaCustomization.addSlide',
                                    )}
                                    value={newSlideLayout}
                                    onChange={e =>
                                        setNewSlideLayout(
                                            e.target.value as CustomReportSlideConfig['layout'],
                                        )
                                    }
                                    size="small"
                                    fullWidth
                                    sx={compactFieldSx}
                                >
                                    {slideLayoutOptions.map(option => (
                                        <MenuItem key={option} value={option}>
                                            {optionLabel(option, t)}
                                        </MenuItem>
                                    ))}
                                </TextField>
                                <Stack direction="row" gap={1}>
                                    <Button
                                        onClick={addSlide}
                                        startIcon={<Add fontSize="small" />}
                                        disabled={!newSlideLayout}
                                    >
                                        {t('Modules.Main.Campaigns.Overview.PcaCustomization.add')}
                                    </Button>
                                    <Button
                                        onClick={removeSelectedSlide}
                                        startIcon={<Delete fontSize="small" />}
                                        disabled={!selectedSlide || draftConfig.slides.length === 0}
                                    >
                                        {t(
                                            'Modules.Main.Campaigns.Overview.PcaCustomization.remove',
                                        )}
                                    </Button>
                                </Stack>
                            </Stack>
                            <Stack gap={0.75} sx={{ maxHeight: 360, overflowY: 'auto', pr: 0.5 }}>
                                {draftConfig.slides.map((slide, index) => (
                                    <Box
                                        key={slide.id}
                                        onClick={() => setSelectedSlideId(slide.id)}
                                        sx={{
                                            border: '1px solid',
                                            borderColor:
                                                slide.id === selectedSlide?.id
                                                    ? 'primary.main'
                                                    : 'divider',
                                            borderRadius: 1.5,
                                            p: 1.25,
                                            cursor: 'pointer',
                                            bgcolor:
                                                slide.id === selectedSlide?.id
                                                    ? 'action.selected'
                                                    : 'background.paper',
                                        }}
                                    >
                                        <Typography variant="subtitle2">
                                            {index + 1}. {getSlideLabel(slide)}
                                        </Typography>
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            justifyContent="space-between"
                                            gap={1}
                                        >
                                            <Typography variant="caption" color="text.secondary">
                                                {toLabel(slide.layout)}
                                            </Typography>
                                            <Stack direction="row" gap={0.5}>
                                                <Tooltip
                                                    title={t(
                                                        'Modules.Main.Campaigns.Overview.PcaCustomization.previewSlide',
                                                    )}
                                                >
                                                    <IconButton
                                                        size="small"
                                                        onClick={e => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            openSlidePreview(slide);
                                                        }}
                                                    >
                                                        <Visibility fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Stack>
                                        </Stack>
                                    </Box>
                                ))}
                            </Stack>
                        </Stack>

                        <Stack gap={2} flex={1} minWidth={0}>
                            <Typography variant="h5">
                                {t(
                                    'Modules.Main.Campaigns.Overview.PcaCustomization.slideSettings',
                                )}
                            </Typography>
                            {renderSelectedSlideEditor()}

                            <Typography variant="h5" sx={{ mt: 1 }}>
                                {t('Modules.Main.Campaigns.Overview.PcaCustomization.jsonPreview')}
                            </Typography>
                            <TextField
                                multiline
                                minRows={16}
                                maxRows={16}
                                value={JSON.stringify(draftConfig, null, 4)}
                                fullWidth
                                InputProps={{
                                    readOnly: true,
                                    sx: {
                                        alignItems: 'flex-start',
                                        fontFamily: 'monospace',
                                        fontSize: 12,
                                    },
                                }}
                            />
                        </Stack>
                    </Stack>
                </Stack>
            </DialogV2>
        </>
    );
};

export default PCACustomizationDialog;
